import React from 'react';
import {gql, useMutation, MutationFunctionOptions} from '@apollo/client';
import {Box, Button, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import {FormikErrors} from 'formik';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {
  UpdateIdentityEmail,
  UpdateIdentityEmailVariables,
} from '../../schema-types/UpdateIdentityEmail';
import {UpdateEmailForm, UpdateEmailFormValues} from './UpdateEmailForm';
import {COGNITO_USER_FRAGMENT} from '../../graphql/fragments';
import {GET_IDENTITY_USER_QUERY} from './SingleIdentityPage';

const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const UPDATE_IDENTITY_EMAIL_MUTATION = gql`
  ${COGNITO_USER_FRAGMENT}
  mutation UpdateIdentityEmail($newEmail: String!, $subId: String!) {
    updateIdentityEmail(newEmail: $newEmail, subId: $subId) {
      user {
        ...CognitoUserFragment
      }
      ok
      errors
    }
  }
`;

export const useUpdateEmail = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [_onUpdateEmail, {data: updateEmailData, loading: updateEmailLoading}] =
    useMutation<UpdateIdentityEmail, UpdateIdentityEmailVariables>(
      UPDATE_IDENTITY_EMAIL_MUTATION,
      {
        refetchQueries: [GET_IDENTITY_USER_QUERY],
      }
    );

  const onUpdateEmail = async (
    options?: MutationFunctionOptions<UpdateIdentityEmail, UpdateIdentityEmailVariables>
  ) => {
    closeSnackbar();
    const key = enqueueSnackbar('Updating email...', {variant: 'info', persist: true});

    try {
      const result = await _onUpdateEmail(options);
      closeSnackbar(key);
      if (result.data?.updateIdentityEmail?.ok) {
        enqueueSnackbar('Email updated successfully', {variant: 'success'});
      } else {
        const errorMsg = result.data?.updateIdentityEmail?.errors?.[0] || 'Unknown error';
        enqueueSnackbar(`Failed to update email: ${errorMsg}`, {variant: 'error'});
      }
      return result;
    } catch (e) {
      closeSnackbar(key);
      enqueueSnackbar(
        `Failed to update email: ${e instanceof Error ? e.message : 'Unknown error'}`,
        {variant: 'error'}
      );
      throw e;
    }
  };

  return {onUpdateEmail, updateEmailData, updateEmailLoading};
};

interface UpdateEmailProps {
  open: boolean;
  onClose: () => void;
  subId?: string | null;
  disabled?: boolean;
  currentEmail?: string | null;
}

export const UpdateEmail: React.FC<UpdateEmailProps> = ({
  open,
  onClose,
  subId,
  disabled,
  currentEmail,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {onUpdateEmail} = useUpdateEmail();

  return (
    <ScrollableModal
      open={open}
      onClose={onClose}
      size="small"
      autoHeight
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1} pt={2}>
          <Button onClick={onClose}>Cancel</Button>
        </Box>
      }
    >
      <>
        <Typography variant="h6" gutterBottom>
          Update Email
        </Typography>
        <UpdateEmailForm
          formFields={[]}
          initialValueOverrides={{
            currentEmail: currentEmail || '',
          }}
          validate={(values) => {
            const errors: FormikErrors<UpdateEmailFormValues> = {};
            if (!values.newEmail) {
              errors.newEmail = 'Required';
            } else if (!validateEmail(values.newEmail)) {
              errors.newEmail = 'Enter a valid email';
            }
            if (!values.confirmEmail) {
              errors.confirmEmail = 'Required';
            } else if (values.confirmEmail !== values.newEmail) {
              errors.confirmEmail = 'Emails do not match';
            }
            return errors;
          }}
          submit={(values) => {
            if (subId && values.newEmail) {
              closeSnackbar();
              const key = enqueueSnackbar('Updating email...', {
                variant: 'info',
                persist: true,
              });
              onUpdateEmail({variables: {subId, newEmail: values.newEmail}})
                .then((result) => {
                  closeSnackbar(key);
                  if (result.data?.updateIdentityEmail?.ok) {
                    enqueueSnackbar('Email updated successfully', {variant: 'success'});
                    onClose();
                  } else {
                    const errorMsg =
                      result.data?.updateIdentityEmail?.errors?.[0] || 'Unknown error';
                    enqueueSnackbar(`Failed to update email: ${errorMsg}`, {
                      variant: 'error',
                    });
                  }
                })
                .catch((e) => {
                  closeSnackbar(key);
                  enqueueSnackbar(
                    `Failed to update email: ${
                      e instanceof Error ? e.message : 'Unknown error'
                    }`,
                    {variant: 'error'}
                  );
                });
            }
          }}
        />
      </>
    </ScrollableModal>
  );
};
